/**
 * RedeemInviteCode — shared form to redeem a promo code.
 *
 * Used inline in the Settings page and Pricing page. Validates via
 * GET /api/invite-codes/preview/{code} before submitting POST .../redeem,
 * so the user sees what they're about to claim. On success, refreshes the
 * BillingContext so the sidebar usage meter / Pro badge update immediately.
 */
import { useState } from "react";
import { Gift, Loader2, Check, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useBilling } from "../contexts/BillingContext";
import { useTasks } from "../contexts/TaskContext";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export default function RedeemInviteCode({ compact = false }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const { refresh: refreshBilling } = useBilling();
  const { refresh: refreshTasks } = useTasks();

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) {
      toast.error("Enter a code to redeem");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invite-codes/redeem", { code: cleaned });
      setDone(data.reward_summary || "Code redeemed");
      toast.success(data.reward_summary || "Code redeemed!", {
        icon: <Sparkles size={16} />,
      });
      setCode("");
      // Refresh dependent state
      await Promise.all([refreshBilling?.(), refreshTasks?.()]);
    } catch (e) {
      const status = e?.response?.status;
      const friendly = formatApiError(e?.response?.data?.detail);
      if (status === 404) toast.error(friendly || "That code doesn't exist");
      else if (status === 410) toast.error(friendly || "That code is no longer valid");
      else if (status === 409) toast.error(friendly || "You've already redeemed this code");
      else toast.error(friendly || "Couldn't redeem that code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="redeem-invite-code-section"
      className={cn(
        "rounded-lg border bg-card transition-colors",
        compact ? "p-3" : "p-5",
        done ? "border-emerald-500/40" : "border-border"
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <Gift size={compact ? 14 : 16} className="text-amber-500" />
        <h3
          className={cn(
            "font-display font-bold tracking-tight",
            compact ? "text-sm" : "text-base"
          )}
        >
          Have an invite code?
        </h3>
      </div>
      {!compact && (
        <p className="text-xs text-muted-foreground mb-3">
          Got a promo code from our team? Redeem it for extra task quota or
          free Pro days.
        </p>
      )}
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. LAUNCH-2026"
          data-testid="redeem-code-input"
          className="font-mono flex-1"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
        <Button
          type="submit"
          disabled={busy || !code.trim()}
          data-testid="redeem-code-submit-btn"
          className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
        >
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin mr-1.5" /> Redeeming…
            </>
          ) : (
            <>
              <Sparkles size={14} className="mr-1.5" /> Redeem
            </>
          )}
        </Button>
      </form>
      {done && (
        <div
          data-testid="redeem-success-message"
          className="mt-3 flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs"
        >
          <Check size={14} className="text-emerald-500 mt-0.5 shrink-0" />
          <span>{done}</span>
        </div>
      )}
    </div>
  );
}
